import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Upload, Eye, Download, RefreshCw, Trash2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const DOCS = [
  { type: 'factura', label: 'Factura de compra', field: 'purchase_invoice_doc' },
  { type: 'albaran', label: 'Albarán de compra', field: 'purchase_albaran_doc' },
];

const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

export default function PurchaseDocs({ bicycle, isAdmin, onUpdated }) {
  const facturaRef = useRef(null);
  const albaranRef = useRef(null);
  const [uploading, setUploading] = useState(null);
  const refs = { factura: facturaRef, albaran: albaranRef };

  if (!isAdmin) return null;

  const upload = async (type, file) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) { toast.error('Solo se permiten PDF, JPG o PNG'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(type);
    try {
      await axios.post(`${API}/bicycles/${bicycle.id}/purchase-doc/${type}`, fd);
      toast.success('Documento guardado');
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir el documento');
    } finally {
      setUploading(null);
    }
  };

  const remove = async (type, label) => {
    if (!window.confirm(`¿Eliminar el documento "${label}" de esta bicicleta?`)) return;
    try {
      await axios.delete(`${API}/bicycles/${bicycle.id}/purchase-doc/${type}`);
      toast.success('Documento eliminado');
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const fullUrl = (doc) => (doc.url.startsWith('http') ? doc.url : `${BACKEND}${doc.url}`);

  const download = (doc) => {
    const a = document.createElement('a');
    a.href = fullUrl(doc);
    a.download = doc.filename || 'documento';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-2" data-testid="purchase-docs">
      {DOCS.map(({ type, label, field }) => {
        const doc = bicycle[field];
        return (
          <div key={type} className="flex items-center gap-2 text-sm flex-wrap" data-testid={`purchase-doc-${type}`}>
            <FileText size={14} className="text-slate-400 shrink-0" />
            <span className="text-slate-500">{label}:</span>
            <input ref={refs[type]} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
              onChange={(e) => { upload(type, e.target.files?.[0]); e.target.value = ''; }}
              data-testid={`purchase-doc-input-${type}`} />
            {uploading === type ? (
              <span className="flex items-center gap-1 text-slate-400"><Loader2 size={13} className="animate-spin" />Subiendo...</span>
            ) : doc ? (
              <>
                <span className="font-medium truncate max-w-[160px]" title={doc.filename}>{doc.filename}</span>
                <div className="flex gap-0.5">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-slate-900" title="Ver/abrir"
                    onClick={() => window.open(fullUrl(doc), '_blank')} data-testid={`view-doc-${type}`}>
                    <Eye size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-slate-900" title="Descargar"
                    onClick={() => download(doc)} data-testid={`download-doc-${type}`}>
                    <Download size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-slate-900" title="Sustituir"
                    onClick={() => refs[type].current?.click()} data-testid={`replace-doc-${type}`}>
                    <RefreshCw size={14} />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" title="Eliminar"
                    onClick={() => remove(type, label)} data-testid={`delete-doc-${type}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                onClick={() => refs[type].current?.click()} data-testid={`upload-doc-${type}`}>
                <Upload size={12} />
                Subir (PDF/JPG/PNG)
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
